import React, { useState } from 'react';
import { EMAIL_REGEX } from 'constants';
import { Formik } from 'formik';
import {
  error as renderError,
  success as renderSuccess,
} from 'helpers/toaster.js';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { setLoader } from 'redux/slices/loader';
import * as Yup from 'yup';
import Styles from './AddMemberModal.module.scss';
import { FiMail, FiX } from 'react-icons/fi';
import { addTeamUser, updateTeamUserAccess } from 'services/teamService';

const options = [
  {
    value: 'admin',
    label: 'Admin',
    description: 'Can add, update and remove team members',
  },
  {
    value: 'creator',
    label: 'Member',
    description: 'Can view team members only',
  },
];

const formatOptionLabel = ({ label, description }, { context }) => {
  if (context === 'menu') {
    return (
      <div className={Styles.selectLabel}>
        <p className={Styles.optionTitle}>{label}</p>
        <span className={Styles.optionDescription}>{description}</span>
      </div>
    );
  }
  // Show compact version when selected
  return (
    <div className={Styles.selectedLabel}>
      <span className={Styles.selectedTitle}>{label}</span>
    </div>
  );
};

const selectStyles = {
  option: (styles, { isSelected, isFocused }) => ({
    ...styles,
    padding: '10px 14px',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: "'Inter', sans-serif",
    color: isSelected ? '#00a7b7' : '#344054',
    backgroundColor: isFocused ? '#F9FAFB' : '#FFFFFF',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderLeft: isSelected ? '3px solid #00a7b7' : '3px solid transparent',
    '&:active': {
      backgroundColor: '#F2F4F7',
    },
  }),
  control: (baseStyles, { isFocused }) => ({
    ...baseStyles,
    minHeight: '42px',
    border: 'none',
    boxShadow: 'none',
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    backgroundColor: 'transparent',
    '&:hover': {
      border: 'none',
    },
  }),
  menu: (baseStyles) => ({
    ...baseStyles,
    borderRadius: '8px',
    boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
    border: '1px solid #E4E7EC',
    overflow: 'hidden',
    marginTop: '4px',
    zIndex: 9999,
  }),
  menuPortal: (baseStyles) => ({
    ...baseStyles,
    zIndex: 9999,
  }),
  menuList: (baseStyles) => ({
    ...baseStyles,
    padding: '4px',
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    color: '#344054',
    fontWeight: '500',
    fontFamily: "'Inter', sans-serif",
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    color: '#98A2B3',
    fontFamily: "'Inter', sans-serif",
  }),
  dropdownIndicator: (baseStyles, { isFocused }) => ({
    ...baseStyles,
    color: isFocused ? '#00a7b7' : '#667085',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: '#00a7b7',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    padding: '0 8px',
  }),
};

function AddMemberModal(props) {
  const { access, email, name, editName } = props.teamUser || {};
  const { team } = props || {};
  const dispatch = useDispatch();
  const [apiError, setApiError] = useState('');

  const initialValues = {
    email: email || '',
    access: access || '',
    name: name || '',
  };

  const validationSchema = Yup.object({
    email: Yup.string().matches(EMAIL_REGEX, 'Invalid email').required('Email is required'),
    name: Yup.string().trim().required('Name is required'),
    access: Yup.string().required('Please select a permission'),
  });

  const handleSubmit = async (values) => {
    try {
      const teamUser = {
        email: values.email.toLowerCase(),
        access: values.access,
        name: values.name,
      };

      const isTeamUser = props.teamUsers?.some(
        (data) => data?.user?.email === values.email,
      );
      const isPendingUser = props.pendingUsers?.some(
        (user) => user?.email === values.email,
      );

      if (isTeamUser) {
        renderError(`The user ${values.email} is already a member.`);
        return;
      } else if (isPendingUser) {
        renderError(`The user ${values.email} already has a pending invite.`);
        return;
      }

      dispatch(
        setLoader({
          isLoading: true,
          heading: 'Adding',
          description: 'Please wait...',
        }),
      );

      if (access) {
        await updateTeamUserAccess(team.id, teamUser);
        renderSuccess('Member access updated successfully');
      } else {
        await addTeamUser(team.id, teamUser);
        renderSuccess('Invitation sent successfully');
      }

      dispatch(setLoader({ isLoading: false }));
      props.onHide();
      props.fetchTeamDetail();
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        'An error occurred while adding the team member.';

      if (
        error.response?.data?.identifier ===
        'team_member_has_active_subscription'
      ) {
        setApiError(errorMessage);
        dispatch(setLoader({ isLoading: false }));
      } else {
        dispatch(setLoader({ isLoading: false }));
        renderError(error);
      }
    }
  };

  return (
    <Modal
      {...props}
      size="md"
      aria-labelledby="add-member-modal"
      centered
      dialogClassName={Styles.addMemberModal}
    >
      <div className={Styles.modalHeader}>
        <h2>{access ? 'Update' : 'Add'} Team Member</h2>
        <button className={Styles.closeButton} onClick={props.onHide}>
          <FiX size={20} />
        </button>
      </div>

      <div className={Styles.modalBody}>
        {apiError && (
          <div className={Styles.alert}>
            <p>{apiError}</p>
          </div>
        )}

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnMount={true}
          enableReinitialize={true}
        >
          {({ values, isValid, dirty, setFieldValue, errors, touched }) => (
            <>
              <div className={Styles.formGroup}>
                <label className={Styles.label}>Name</label>
                <input
                  type="text"
                  className={Styles.input}
                  placeholder="Enter name"
                  value={values.name}
                  onChange={(e) => setFieldValue('name', e.target.value)}
                  disabled={!!(props.teamUser && editName)}
                />
                {errors.name && touched.name && (
                  <span className={Styles.error}>{errors.name}</span>
                )}
              </div>

              <div className={Styles.emailGroup}>
                <label className={Styles.label}>Email</label>
                <div className={Styles.inputWrapper}>
                  <div className={Styles.iconWrapper}>
                    <FiMail size={18} />
                  </div>
                  <input
                    type="email"
                    className={Styles.emailInput}
                    placeholder="Enter email for invite"
                    value={values.email}
                    onChange={(e) => setFieldValue('email', e.target.value)}
                    disabled={!!access}
                  />
                  <div className={Styles.permissionSelect}>
                    <Select
                      isSearchable={false}
                      styles={selectStyles}
                      options={options}
                      placeholder="Select Permission"
                      onChange={({ value }) => setFieldValue('access', value)}
                      value={options.find((option) => option.value === values.access) || null}
                      menuPlacement="auto"
                      menuPortalTarget={document.body}
                      formatOptionLabel={formatOptionLabel}
                    />
                  </div>
                </div>
                {errors.email && touched.email && (
                  <span className={Styles.error}>{errors.email}</span>
                )}
              </div>

              <div className={Styles.modalFooter}>
                <button
                  type="button"
                  className={Styles.cancelButton}
                  onClick={props.onHide}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={Styles.submitButton}
                  onClick={() => handleSubmit(values)}
                  disabled={!isValid || !dirty}
                >
                  {access ? 'Update' : 'Add'}
                </button>
              </div>
            </>
          )}
        </Formik>
      </div>
    </Modal>
  );
}

export default AddMemberModal;
